import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  sendCode, verifyCode, setSession, getSession, createTransfer, uploadFile, uploadFileE2E, sendTransfer, resolveFileRequest, getReturnContext, getQuota, listRecentRecipients, lookupRecipientCodes, acceptBaa, setMarketing, saveDisplayName,
} from '../api';
import { generateTransferKey, wrapKeyWithPin } from '../utils/e2eCrypto';
import { logEvent } from '../utils/analytics';
import TrustBadges from '../components/TrustBadges';
import ShareWithColleague from '../components/ShareWithColleague';
import TellAColleague from '../components/TellAColleague';
import QuotaNotice, { StoreUpsellLine } from '../components/QuotaNotice';

// The framework designer, shared with the practice dashboard and Bridge chat so
// one Rx cannot be drawn two different ways. Lazy because it carries the full
// tooth-anatomy geometry, and only a removable partial ever opens it.
//
// Subpath import on purpose: the package root also exports PdfViewer and
// FileUpload, which resolve '@/components/ui/*' against the CONSUMING app and
// therefore only build inside practice-dashboard.
const RpdDesigner = lazy(() => import('@intake-dental/shared-components/components/RpdDesigner'));

const MAX_FILE = 10 * 1024 * 1024 * 1024; // 10 GB

// Mirrors SendLabRxService's vocabularies. Kept in the same order as the
// service's arrays so a mismatch is obvious on sight.
const LAB_CASE_TYPES = [
  ['partial-denture', 'Removable partial denture'],
  ['full-denture', 'Full denture'],
  ['crown-bridge', 'Crown / bridge'],
  ['night-guard', 'Night guard'],
  ['retainer', 'Retainer'],
  ['implant-restoration', 'Implant restoration'],
  ['repair', 'Repair'],
  ['other', 'Other, see message'],
];
const LAB_MATERIALS = [
  ['chrome-cobalt', 'Chrome cobalt'],
  ['acrylic', 'Acrylic'],
  ['flexible', 'Flexible (nylon)'],
  ['titanium', 'Titanium'],
  ['zirconia', 'Zirconia'],
  ['emax', 'e.max / lithium disilicate'],
  ['pfm', 'Porcelain fused to metal'],
  ['gold', 'Gold'],
  ['other', 'Other, see message'],
];
// Only a removable partial gets a framework tracing. Matches
// DESIGN_CASE_TYPES in SendLabRxService, which drops a design sent for
// anything else.
const DESIGN_CASE_TYPES = new Set(['partial-denture']);

export default function UploadPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(() => getSession()?.token ? 'compose' : 'email');

  // ?request=<token> — someone asked this visitor to send THEM files.
  // Resolve to the requester's name/email so compose pre-fills the recipient.
  const [searchParams] = useSearchParams();
  const requestToken = searchParams.get('request');
  // "Send again to…" from the dashboard lands here with the recipient attached.
  const prefillTo = searchParams.get('to');
  const prefillName = searchParams.get('name') || '';
  // Legacy case-conversation links land on the root with ?space=&thread= —
  // route them to the Spaces view they were always meant for.
  const deepSpace = searchParams.get('space');
  useEffect(() => {
    if (deepSpace) nav(`/spaces${window.location.search}`, { replace: true });
  }, [deepSpace, nav]);

  // ?return=<access token> — "Send files back" from a package this visitor
  // received: back to the office that sent it, into the same case.
  const returnToken = searchParams.get('return');
  const [returnInfo, setReturnInfo] = useState(null);
  const [returnErr, setReturnErr] = useState(null);
  useEffect(() => {
    if (!returnToken) return;
    getReturnContext(returnToken)
      .then((r) => setReturnInfo({ token: returnToken, toEmail: r.to_email, office: r.office_name || r.to_email, recipientEmail: r.recipient_email }))
      .catch((e) => setReturnErr(e.message || 'That package is no longer available.'));
  }, [returnToken]);

  const [fileRequest, setFileRequest] = useState(null);
  const [sentInfo, setSentInfo] = useState(null);
  const [welcomeBack, setWelcomeBack] = useState(false);
  useEffect(() => {
    if (!requestToken) return;
    resolveFileRequest(requestToken)
      .then((r) => setFileRequest({ token: requestToken, email: r.requester_email, name: r.requester_name }))
      .catch(() => setFileRequest(null));
  }, [requestToken]);

  return (
    <div className="page">
      {fileRequest && (
        <div className="promo">
          <div>
            <strong>{fileRequest.name || fileRequest.email}</strong> asked you to send them files —
            their address will be pre-filled below.
          </div>
        </div>
      )}
      {returnInfo && (
        <div className="promo">
          <div>
            <strong>Sending files back to {returnInfo.office}.</strong> They are filed in the same case,
            and returns don't count toward your free sends.
          </div>
        </div>
      )}
      {returnErr && <div className="error" style={{ marginBottom: 12 }}>Can't send files back: {returnErr} You can still start a new transfer below.</div>}
      {step === 'email'   && <EmailStep initialEmail={returnInfo?.recipientEmail || ''} onVerified={(info) => { setWelcomeBack(!!info?.returning); setStep('compose'); }} />}
      {step === 'compose' && <ComposeStep welcomeBack={welcomeBack} fileRequest={fileRequest} returnInfo={returnInfo} prefillTo={prefillTo} prefillName={prefillName} onSent={(info) => { setSentInfo(info || null); setStep('sent'); }} onSignOut={() => setStep('email')} />}
      {step === 'sent'    && <SentStep sentInfo={sentInfo} onAnother={() => setStep('compose')} onDashboard={() => nav('/dashboard')} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 1: email + code
// ═══════════════════════════════════════════════════════════════════════════

export function EmailStep({ onVerified, initialEmail = '' }) {
  const [email, setEmail] = useState(initialEmail);
  useEffect(() => { if (initialEmail) setEmail((e) => e || initialEmail); }, [initialEmail]);
  const [code, setCode]   = useState('');
  const [sent, setSent]   = useState(false);
  const [baa, setBaa]     = useState(false);
  const [optin, setOptin] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const onSend = async () => {
    setErr(null); setLoading(true);
    try {
      await sendCode(email);
      setSent(true);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  // Code first. Someone who has accepted the BAA before goes straight in;
  // only a first-time sender sees the BAA / name / updates screen. Asking
  // everyone, every sign-in, made returning senders feel brand new. (The
  // server refuses to create a transfer until the BAA is accepted.)
  const [setup, setSetup] = useState(false);
  const onVerify = async () => {
    setErr(null);
    setLoading(true);
    try {
      const r = await verifyCode({ email, code });
      setSession({ token: r.token, user: r.user });
      if (r.user?.baa_accepted_at) onVerified({ returning: true });
      else setSetup(true);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const onFinishSetup = async () => {
    setErr(null);
    if (!baa) { setErr('Please accept the BAA to continue.'); return; }
    setLoading(true);
    try {
      await acceptBaa();
      if (optin) await setMarketing(true).catch(() => {});
      if (displayName.trim()) await saveDisplayName(displayName.trim()).catch(() => {});
      const s = getSession();
      if (s) setSession({ ...s, user: { ...(s.user || {}), baa_accepted_at: new Date().toISOString(), display_name: displayName.trim() || s.user?.display_name || null } });
      onVerified({ returning: false });
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  return (
    <div className="landing">
      <section className="hero">
        <div className="eyebrow">Secure file transfer for dental practices</div>
        <h1>Send x-rays and patient files <br/>without the HIPAA headache.</h1>
        <p className="hero-lead">
          Free, BAA-backed file transfer built for dentists. Drop a CBCT, intraoral
          scan, or DICOM study and share it with a specialist in under a minute.
        </p>
        <TrustBadges />
      </section>

      <div className="narrow card hero-card">
        {!sent ? (
          <>
            <h2 className="card-title centered">Get started</h2>
            <p className="muted centered" style={{ marginTop: -2, marginBottom: 18 }}>
              Enter your work email — no account or password to manage.
            </p>
            <label className="field">
              <span>Your work email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourpractice.com"
                autoFocus
              />
            </label>
            <button className="btn btn-primary btn-block btn-large" onClick={onSend} disabled={!email || loading}>
              {loading ? 'Sending…' : 'Email me a 6-digit code'}
            </button>
            {err && <div className="error centered">{err}</div>}
          </>
        ) : setup ? (
          <>
            <h2 className="card-title centered">One-time setup</h2>
            <p className="muted centered" style={{ marginTop: -2, marginBottom: 18 }}>
              First time sending from <strong>{email}</strong>. You won't see this again.
            </p>
            <label className="field">
              <span>Your name (optional)</span>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Dr. Jane Smith" autoFocus />
            </label>
            <label className="checkbox">
              <input type="checkbox" checked={baa} onChange={(e) => setBaa(e.target.checked)} />
              <span>
                I accept Intake.Dental's Business Associate Agreement (BAA) and Terms of Service
                for this secure file transfer. <a href="https://intake.dental/baa" target="_blank" rel="noreferrer">View BAA</a>
              </span>
            </label>
            <label className="checkbox">
              <input type="checkbox" checked={optin} onChange={(e) => setOptin(e.target.checked)} />
              <span>Send me occasional product updates and dental tech tips from Intake.Dental. (optional)</span>
            </label>
            <button className="btn btn-primary btn-block btn-large" onClick={onFinishSetup} disabled={loading}>
              {loading ? 'Saving…' : 'Continue'}
            </button>
            {err && <div className="error centered">{err}</div>}
          </>
        ) : (
          <>
            <h2 className="card-title centered">Check your inbox</h2>
            <p className="muted centered" style={{ marginTop: -2, marginBottom: 18 }}>
              We sent a 6-digit code to <strong>{email}</strong>.
            </p>
            <label className="field">
              <span>Verification code</span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoFocus
                style={{ fontSize: 22, letterSpacing: 4, textAlign: 'center' }}
              />
            </label>

            <button className="btn btn-primary btn-block btn-large" onClick={onVerify} disabled={code.length !== 6 || loading}>
              {loading ? 'Verifying…' : 'Verify & continue'}
            </button>
            <button className="btn btn-secondary btn-block" style={{ marginTop: 8 }} onClick={() => setSent(false)}>
              Use a different email
            </button>
            {err && <div className="error centered">{err}</div>}
          </>
        )}
      </div>

      <section className="how-it-works">
        <h2 className="centered">How it works</h2>
        <p className="muted centered" style={{ marginBottom: 28 }}>From upload to specialist in three steps.</p>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-num">1</div>
            <h3>Verify your email</h3>
            <p>One-time 6-digit code, no password. Accept the BAA once and you're in.</p>
          </div>
          <div className="step-card">
            <div className="step-num">2</div>
            <h3>Drop your files</h3>
            <p>DICOM, CBCT, intraoral scans, PDFs — anything up to 10 GB per file.</p>
          </div>
          <div className="step-card">
            <div className="step-num">3</div>
            <h3>Share with a PIN</h3>
            <p>We email a secure link. You text the recipient a 4-digit PIN — we never email the PIN.</p>
          </div>
        </div>
      </section>

      {/* Public, logged-out, and asks for nothing. Every visitor who already
          refers cases is one office away from another practice that needs
          this; the landing page previously gave them no way to pass it on. */}
      <TellAColleague />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
/**
 * The offices this sender already sends to, newest first. Derived from their own
 * transfer history rather than a saved address book, so there is nothing to
 * curate and it works for everyone who has ever sent — including retroactively.
 *
 * Renders nothing on a first send, which is exactly when it would be noise.
 */
function RecentRecipients({ onPick, chosen = [] }) {
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    // A failure here must not touch the send flow — worst case the shortcut is
    // simply absent.
    listRecentRecipients()
      .then((r) => setRecent(r.recipients || []))
      .catch(() => setRecent([]));
  }, []);

  const available = recent.filter((r) => !chosen.includes(r.email.toLowerCase()));
  if (!available.length) return null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>Send again to</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {available.map((r) => (
          <button
            key={r.email}
            type="button"
            className="btn btn-secondary"
            onClick={() => onPick(r)}
            title={r.email}
            style={{ padding: '5px 11px', fontSize: 13, maxWidth: '100%' }}
          >
            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.name || r.email}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Step 2: compose (upload + recipients + PIN)
// ═══════════════════════════════════════════════════════════════════════════

// onSignOut was passed by the parent and not destructured here, so the one
// path back to the sign-in step could never fire.
function ComposeStep({ fileRequest = null, returnInfo = null, prefillTo = null, prefillName = '', onSent, onSignOut, welcomeBack = false }) {
  const session = getSession();
  const user = session?.user;
  const [files, setFiles]       = useState([]); // { file, progress, fileId?, done }
  const [title, setTitle]       = useState('');
  const [message, setMessage]   = useState('');
  const [recipients, setRecipients] = useState([{ email: '', name: '' }]);
  const [quotaHit, setQuotaHit] = useState(false);
  // Referral context. Closed vocabularies only — anything narrative goes in the
  // existing message field, which is the one place already meant for it.
  // ?referral=1&specialty=<x> (a directory listing's "Refer a patient" link
  // for an office without its own referral form) pre-toggles the block.
  const [isReferral, setIsReferral] = useState(() => new URLSearchParams(window.location.search).get('referral') === '1');
  const [referral, setReferral] = useState(() => {
    const wanted = (new URLSearchParams(window.location.search).get('specialty') || '').trim().toLowerCase();
    const known = ['oral-surgery', 'endo', 'perio', 'ortho', 'pedo', 'prostho', 'implants', 'cosmetic'];
    // The directory speaks `oral_surgery`; the select speaks `oral-surgery`.
    const specialty = known.includes(wanted) ? wanted : (known.includes(wanted.replace(/_/g, '-')) ? wanted.replace(/_/g, '-') : '');
    return { urgency: 'routine', specialty, reason: '' };
  });

  // Lab prescription context, the same idea as the referral block above: an Rx
  // is a transfer with context, not a separate product. Before this, a dentist
  // without a dashboard could attach the scan and then describe the case in the
  // message box, which is how a partial comes back with the wrong clasp.
  // ?labrx=1 (and &case=<type>) pre-toggles it, for the lab Rx landing page.
  const [isLabRx, setIsLabRx] = useState(() => new URLSearchParams(window.location.search).get('labrx') === '1');
  const [labRx, setLabRx] = useState(() => {
    const wanted = (new URLSearchParams(window.location.search).get('case') || '').trim().toLowerCase();
    const known = LAB_CASE_TYPES.map(([v]) => v);
    return {
      caseType: known.includes(wanted) ? wanted : 'partial-denture',
      material: '',
      shade: '',
      dueDate: '',
    };
  });
  const [rpdDesign, setRpdDesign] = useState(null);
  const showDesigner = isLabRx && DESIGN_CASE_TYPES.has(labRx.caseType);

  // A package is a referral OR an Rx, never both. The DB refuses the pair, so
  // the UI must not be able to ask for it. Each toggle clears the other.
  const toggleReferral = (on) => { setIsReferral(on); if (on) setIsLabRx(false); };
  const toggleLabRx = (on) => { setIsLabRx(on); if (on) setIsReferral(false); };

  // Pre-fill the requester as recipient when arriving via a request link
  // (only over an untouched blank row — never clobber typed input).
  useEffect(() => {
    if (!fileRequest?.email) return;
    setRecipients((prev) =>
      prev.length === 1 && !prev[0].email && !prev[0].name
        ? [{ email: fileRequest.email, name: fileRequest.name || '' }]
        : prev);
  }, [fileRequest]);

  // A return goes to the office that sent the package, and only there.
  useEffect(() => {
    if (!returnInfo?.toEmail) return;
    setRecipients([{ email: returnInfo.toEmail, name: returnInfo.office && returnInfo.office !== returnInfo.toEmail ? returnInfo.office : '' }]);
  }, [returnInfo]);
  const returnAsOther = !!returnInfo && !!user?.email && !!returnInfo.recipientEmail
    && user.email.trim().toLowerCase() !== returnInfo.recipientEmail.trim().toLowerCase();

  // Same treatment for a "send again to…" deep link from the dashboard.
  useEffect(() => {
    if (!prefillTo) return;
    setRecipients((prev) =>
      prev.length === 1 && !prev[0].email && !prev[0].name
        ? [{ email: prefillTo, name: prefillName || '' }]
        : prev);
  }, [prefillTo, prefillName]);
  const [pin, setPin]           = useState('');
  // The code used last time for these recipients, filled in so the sender
  // doesn't invent (and phone through) a new one every send.
  const [savedCode, setSavedCode] = useState(null);
  const savedCodeRef = useRef(null);
  const [rememberCode, setRememberCode] = useState(true);
  const usingSavedCode = !!savedCode && pin === savedCode;
  const recipientKey = recipients.map((r) => (r.email || '').trim().toLowerCase())
    .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)).sort().join(',');
  useEffect(() => {
    if (!recipientKey) { setSavedCode(null); savedCodeRef.current = null; return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      lookupRecipientCodes(recipientKey.split(','))
        .then((r) => {
          if (cancelled) return;
          const codes = Object.values(r?.codes || {});
          const shared = codes.length && codes.every((c) => c && c === codes[0]) ? codes[0] : null;
          const prev = savedCodeRef.current;
          savedCodeRef.current = shared;
          setSavedCode(shared);
          // Never over a code the sender typed themselves.
          setPin((cur) => (cur === '' || cur === prev ? (shared || '') : cur));
        })
        .catch(() => { /* a convenience, never a blocker */ });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [recipientKey]);
  // Every transfer is end-to-end encrypted — there is no plaintext option.
  // (The server-built packages — referral and lab images — are a separate path.)
  const e2e = true;
  // How long the package stays available. 7 days is the long-standing default
  // and stays the default; the ceiling comes from the sender's plan so the
  // choice offered is always one the server will honour.
  const [expiryDays, setExpiryDays] = useState(7);
  const [maxExpiryDays, setMaxExpiryDays] = useState(30);
  const [quota, setQuota] = useState(null);
  useEffect(() => {
    getQuota()
      .then((r) => {
        if (r?.quota?.max_expiry_days) setMaxExpiryDays(r.quota.max_expiry_days);
        if (r?.quota) setQuota(r.quota);
      })
      .catch(() => { /* the default ceiling is the safe one */ });
  }, []);
  // "3 of 5 free sends left this month" / "Unlimited with <office>'s plan".
  const allowanceLine = (() => {
    if (!quota) return null;
    const office = quota.practice_name || 'your office';
    if (quota.unlimited) return quota.scope === 'practice' ? `Unlimited sends with ${office}'s plan.` : 'Unlimited sends on your plan.';
    const limit = quota.limit || 5;
    const left = Math.max(0, quota.remaining ?? 0);
    const resets = quota.resets_at ? new Date(quota.resets_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null;
    const shared = quota.scope === 'practice' ? `, shared across ${office}` : '';
    if (!left) return `No free sends left this month${shared}${resets ? ` — resets ${resets}` : ''}.`;
    return `${left} of ${limit} free send${limit === 1 ? '' : 's'} left this month${shared}.`;
  })();
  const [stage, setStage]       = useState('idle'); // idle | creating | uploading | sending
  const [err, setErr]           = useState(null);
  const dropRef = useRef();
  const pickerRef = useRef();

  const onPick = (flist) => {
    const next = [...files];
    for (const f of flist) {
      if (f.size > MAX_FILE) { setErr(`${f.name} exceeds the 10 GB limit.`); continue; }
      next.push({ file: f, progress: 0, done: false });
    }
    setFiles(next);
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const updateRecipient = (i, key, value) => {
    const next = [...recipients];
    next[i] = { ...next[i], [key]: value };
    setRecipients(next);
  };
  const addRecipient = () => setRecipients([...recipients, { email: '', name: '' }]);
  const removeRecipient = (i) => setRecipients(recipients.filter((_, idx) => idx !== i));

  const validForSend = files.length > 0 && recipients[0]?.email && /^\d{4}$/.test(pin);

  const onSubmit = async () => {
    setErr(null);
    if (!validForSend) return;

    setStage('creating');
    try {
      // Zero-knowledge path: generate the transfer key in this browser; the
      // server stores only a PIN-wrapped copy. The raw key additionally rides
      // the copy-links' URL fragment, which never reaches any server.
      let tk = null;
      let e2eKeyWrapped = null;
      if (e2e) {
        tk = await generateTransferKey();
        e2eKeyWrapped = await wrapKeyWithPin(tk.rawB64url, pin);
      }
      const { transfer } = await createTransfer({
        title, message,
        expiry_days: expiryDays,
        ...(e2e ? { e2e: true, e2e_key_wrapped: e2eKeyWrapped } : {}),
        ...(isReferral
          ? {
              is_referral: true,
              referral_urgency: referral.urgency,
              ...(referral.specialty ? { referral_specialty: referral.specialty } : {}),
              ...(referral.reason ? { referral_reason_code: referral.reason } : {})
            }
          : {}),
        ...(isLabRx
          ? {
              is_lab_rx: true,
              lab_case_type: labRx.caseType,
              ...(labRx.material ? { lab_material: labRx.material } : {}),
              ...(labRx.shade.trim() ? { lab_shade: labRx.shade.trim() } : {}),
              ...(labRx.dueDate ? { lab_due_date: labRx.dueDate } : {}),
              // Only sent when the case type actually has a framework, so
              // drawing a partial and then switching to a crown cannot ship a
              // tracing the lab has no use for.
              ...(showDesigner && rpdDesign ? { rpd_design: rpdDesign } : {})
            }
          : {})
      });

      setStage('uploading');
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (f.done) continue;
        const onProgress = (done, total) => {
          setFiles(prev => prev.map((x, idx) => idx === i ? { ...x, progress: done / total } : x));
        };
        if (e2e) {
          await uploadFileE2E({ transferId: transfer.id, file: f.file, key: tk.key, onProgress });
        } else {
          await uploadFile({ transferId: transfer.id, file: f.file, onProgress });
        }
        setFiles(prev => prev.map((x, idx) => idx === i ? { ...x, progress: 1, done: true } : x));
      }

      setStage('sending');
      const urlParams = new URLSearchParams(window.location.search);
      const cameFromBridge = urlParams.get('src') === 'bridge_chat';
      // Lab-case flow: ?thread=<send-space thread id> pins the transfer to
      // the exact case conversation the doctor started from.
      const targetThread = urlParams.get('thread');
      const sendResult = await sendTransfer(transfer.id, {
        recipients: recipients.filter(r => r.email).map(r => ({ email: r.email, name: r.name || undefined })),
        pin,
        remember_code: usingSavedCode || rememberCode,
        ...(fileRequest?.token ? { request_token: fileRequest.token } : {}),
        // Suppresses the server's Bridge auto-post — the sender posts the
        // link into their chat themselves via the handoff on the Sent screen.
        ...(cameFromBridge ? { source: 'bridge_chat' } : {}),
        ...(targetThread ? { space_thread_id: targetThread } : {}),
        ...(returnInfo && !returnAsOther ? { return_token: returnInfo.token } : {})
      });

      const sentTo = recipients.filter(r => r.email).map(r => r.email.trim().toLowerCase());
      onSent({
        e2e,
        detailsSealed: sendResult?.details_sealed === true,
        codeReused: sentTo.length > 0 && sentTo.every((e) => (sendResult?.code_reused_for || []).includes(e)),
        links: (sendResult?.recipients || [])
          .filter(r => r.link)
          .map(r => ({ email: r.email, link: e2e && tk ? `${r.link}#k=${tk.rawB64url}` : r.link }))
      });
    } catch (e) {
      // 402 is the free-tier cap, not a failure the user can fix by retrying.
      // Flag it so the UI offers the upgrade rather than a dead-end message.
      if (e.status === 402) setQuotaHit(true);
      // 401 means the API rejected the session and the client has already
      // thrown it away. Without this the screen stays on compose, still
      // believing it is signed in, and every retry fails the same way — the
      // state senders were stuck in after the 2026-09-19 secret rotation.
      // Going back to the email step turns a lockout into one emailed code.
      if (e.status === 401) {
        setErr(e.data?.action ? `${e.message} ${e.data.action}` : e.message);
        setStage('idle');
        if (typeof onSignOut === 'function') onSignOut();
        return;
      }
      setErr(e.message);
      setStage('idle');
    }
  };

  return (
    <div>
      {user?.practice_id && (
        <div className="promo">
          <div>
            <strong>You're an Intake.Dental practice user.</strong>{' '}
            Send from your dashboard to search the patient by name and file every CBCT and
            x-ray to their chart.{' '}
            {quota?.unlimited
              ? `Sending is unlimited on ${quota.practice_name ? `${quota.practice_name}'s` : 'your office\'s'} plan, here or there.`
              : 'Unlimited on Practice Autopilot and AI Dental Office.'}
          </div>
          <a className="btn btn-secondary" href="https://app.intake.dental/secure-transfer/from-patient" target="_blank" rel="noreferrer">
            Send from your dashboard →
          </a>
        </div>
      )}

      {user?.email && (
        <div className="promo" style={{ alignItems: 'center' }}>
          <div>
            {welcomeBack ? <strong>Welcome back{user.display_name ? `, ${user.display_name}` : ''}.</strong> : <strong>Signed in as {user.email}.</strong>}{' '}
            Everything you've sent — who opened it, and when — is in your transfers.
            {allowanceLine && <div style={{ marginTop: 4, fontSize: 13, opacity: 0.85 }}>{allowanceLine}</div>}
          </div>
          <a className="btn btn-secondary" href="/dashboard">Your transfers →</a>
        </div>
      )}

      <div className="card">
        <h1>Upload files</h1>
        <p className="lead">Drag and drop, or click to pick. Up to 10 GB per file.</p>

        <div
          ref={dropRef}
          className="dropzone"
          onClick={() => pickerRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add('over'); }}
          onDragLeave={() => dropRef.current?.classList.remove('over')}
          onDrop={(e) => {
            e.preventDefault();
            dropRef.current?.classList.remove('over');
            onPick(e.dataTransfer.files);
          }}
        >
          <h3>Drop files here</h3>
          <p>DICOM · JPG · PNG · PDF · anything up to 10 GB</p>
          <input ref={pickerRef} type="file" multiple hidden onChange={(e) => onPick(e.target.files)} />
        </div>

        {files.length > 0 && (
          <ul className="files-list">
            {files.map((f, i) => (
              <li key={i}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</div>
                  <div className="muted">{formatBytes(f.file.size)}</div>
                </div>
                {stage === 'uploading' && !f.done && (
                  <div className="progress"><span style={{ width: `${Math.round(f.progress * 100)}%` }} /></div>
                )}
                {stage === 'idle' && <button className="rm" onClick={() => removeFile(i)}>Remove</button>}
                {f.done && <span className="badge badge-green">Done</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {!returnInfo && <QuotaNotice />}

      {/* A referral is a transfer with context, not a separate product: the
          specialist's reply and report rails already exist. This just stops the
          GP having to quote a reference code from another email. */}
      <div className="card">
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={isReferral}
            onChange={(e) => toggleReferral(e.target.checked)}
            style={{ width: 'auto', marginTop: 3 }}
          />
          <span>
            <strong>This is a patient referral</strong>
            <span className="muted" style={{ display: 'block', fontSize: 13 }}>
              Adds urgency and reason to what the specialist sees, and logs it as a referral.
            </span>
          </span>
        </label>

        {isReferral && (
          <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>Urgency</span>
              <select
                value={referral.urgency}
                onChange={(e) => setReferral((p) => ({ ...p, urgency: e.target.value }))}
              >
                <option value="routine">Routine</option>
                <option value="soon">Within 2 weeks</option>
                <option value="urgent">Urgent / in pain</option>
              </select>
            </label>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>Specialty (optional)</span>
              <select
                value={referral.specialty}
                onChange={(e) => setReferral((p) => ({ ...p, specialty: e.target.value }))}
              >
                <option value="">—</option>
                <option value="oral-surgery">Oral Surgery</option>
                <option value="endo">Endodontics</option>
                <option value="perio">Periodontics</option>
                <option value="ortho">Orthodontics</option>
                <option value="pedo">Pediatric Dentistry</option>
                <option value="prostho">Prosthodontics</option>
                <option value="implants">Implants</option>
                <option value="cosmetic">Cosmetic Dentistry</option>
              </select>
            </label>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>Reason (optional)</span>
              <select
                value={referral.reason}
                onChange={(e) => setReferral((p) => ({ ...p, reason: e.target.value }))}
              >
                <option value="">—</option>
                <option value="consult">Consultation</option>
                <option value="treatment">Treatment</option>
                <option value="second-opinion">Second opinion</option>
                <option value="imaging-review">Imaging review</option>
                <option value="co-management">Co-management</option>
                <option value="other">Other — see message</option>
              </select>
            </label>
            <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
              Patient details stay inside the package, behind the PIN — put anything
              specific in the message rather than here.
            </p>
          </div>
        )}
      </div>

      {/* A prescription is a transfer with context too. The lab still needs no
          account and no software: it opens the same link with the same PIN and
          finds the Rx sitting above the files. */}
      <div className="card">
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={isLabRx}
            onChange={(e) => toggleLabRx(e.target.checked)}
            style={{ width: 'auto', marginTop: 3 }}
          />
          <span>
            <strong>This is a lab prescription</strong>
            <span className="muted" style={{ display: 'block', fontSize: 13 }}>
              Says what to make, so the lab is not reading it out of your message.
              Partials get a framework you can draw.
            </span>
          </span>
        </label>

        {isLabRx && (
          <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>Case type</span>
              <select
                value={labRx.caseType}
                onChange={(e) => setLabRx((p) => ({ ...p, caseType: e.target.value }))}
              >
                {LAB_CASE_TYPES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </label>
            <label className="field" style={{ marginBottom: 0 }}>
              <span>Material (optional)</span>
              <select
                value={labRx.material}
                onChange={(e) => setLabRx((p) => ({ ...p, material: e.target.value }))}
              >
                <option value="">—</option>
                {LAB_MATERIALS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </label>
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr' }}>
              <label className="field" style={{ marginBottom: 0 }}>
                <span>Shade (optional)</span>
                <input
                  type="text"
                  value={labRx.shade}
                  maxLength={40}
                  placeholder="A2"
                  onChange={(e) => setLabRx((p) => ({ ...p, shade: e.target.value }))}
                />
              </label>
              <label className="field" style={{ marginBottom: 0 }}>
                <span>Needed by (optional)</span>
                <input
                  type="date"
                  value={labRx.dueDate}
                  onChange={(e) => setLabRx((p) => ({ ...p, dueDate: e.target.value }))}
                />
              </label>
            </div>

            {showDesigner && (
              <div style={{ marginTop: 4 }}>
                <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>Framework</p>
                <Suspense fallback={<p className="muted" style={{ fontSize: 13 }}>Loading the designer…</p>}>
                  <RpdDesigner value={rpdDesign} onChange={setRpdDesign} />
                </Suspense>
                {rpdDesign?.summary && (
                  <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
                    Sending to the lab: {rpdDesign.summary}
                  </p>
                )}
              </div>
            )}

            <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
              The patient is never named here. A framework names teeth and
              components only. Anything specific goes in the message, behind the PIN.
            </p>
          </div>
        )}
      </div>

      <div className="card">
        <h2>{returnInfo ? 'Sending back to' : 'Recipients'}</h2>
        {!returnInfo && <RecentRecipients
          onPick={(rec) => {
            // Fill the first empty row, or append one — tapping a colleague you
            // send to every week should never overwrite something typed.
            setRecipients((prev) => {
              const idx = prev.findIndex((r) => !r.email.trim());
              const row = { email: rec.email, name: rec.name || '' };
              if (idx === -1) return [...prev, row];
              const next = [...prev];
              next[idx] = row;
              return next;
            });
          }}
          chosen={recipients.map((r) => r.email.trim().toLowerCase()).filter(Boolean)}
        />}
        {returnInfo && (
          <div style={{ marginBottom: 8 }}>
            <p style={{ margin: 0 }}>
              <strong>{returnInfo.office}</strong>{returnInfo.office !== returnInfo.toEmail ? <span className="muted"> · {returnInfo.toEmail}</span> : null}
            </p>
            {returnAsOther ? (
              <p className="error" style={{ marginTop: 8 }}>
                This package was sent to {returnInfo.recipientEmail}. Sign in as that address to send files back in the same case,
                or this goes as a new transfer.{' '}
                <button type="button" className="link-button" onClick={onSignOut}>Switch account</button>
              </p>
            ) : (
              <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Filed in the same case as the package they sent you.</p>
            )}
          </div>
        )}
        {!returnInfo && recipients.map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
            <input type="email" placeholder="specialist@office.com"
                   value={r.email} onChange={(e) => updateRecipient(i, 'email', e.target.value)} />
            <input type="text" placeholder="Name (optional)"
                   value={r.name} onChange={(e) => updateRecipient(i, 'name', e.target.value)} />
            {recipients.length > 1
              ? <button className="btn btn-secondary" onClick={() => removeRecipient(i)}>✕</button>
              : <span />}
          </div>
        ))}
        {!returnInfo && <button className="btn btn-secondary" onClick={addRecipient}>+ Add another recipient</button>}
      </div>

      <div className="card">
        <p className="muted" style={{ marginTop: 0, marginBottom: 14, fontSize: 13 }}>
          🔒 Files are encrypted in your browser before upload; we store only ciphertext. Your PIN protects the key.
        </p>
        <h2>Access PIN</h2>
        {usingSavedCode ? (
          <p className="muted" style={{ marginTop: 0 }}>
            <strong>Same code as last time</strong>: this recipient already has it, so there is nothing
            new to share. Their email will say to use the same code.{' '}
            <button type="button" className="link-button" onClick={() => { setPin(''); setRememberCode(true); }}>
              Use a new code
            </button>
          </p>
        ) : (
          <p className="muted" style={{ marginTop: 0 }}>
            Set a 4-digit PIN. <strong>Text or call this to the recipient directly</strong> — we never include it in the email.
          </p>
        )}
        <label className="field narrow" style={{ margin: 0 }}>
          <input
            type="text" inputMode="numeric" maxLength={4}
            value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            placeholder="0000"
            style={{ fontSize: 24, letterSpacing: 6, textAlign: 'center', maxWidth: 160 }}
          />
        </label>
        {!usingSavedCode && /^\d{4}$/.test(pin) && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10, fontSize: 14 }}>
            <input type="checkbox" checked={rememberCode} onChange={(e) => setRememberCode(e.target.checked)} />
            <span>
              Remember this code for {recipients.filter(r => r.email).length > 1 ? 'these recipients' : 'this recipient'}
              <span className="muted" style={{ display: 'block', fontSize: 12 }}>Next time it fills in by itself, so they keep using the same code.</span>
            </span>
          </label>
        )}
      </div>

      <div className="card">
        <h2>Optional details</h2>
        <label className="field">
          <span>Subject (only the sender sees this)</span>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder='e.g. "Jane Doe — CBCT for implant eval"' />
        </label>
        <label className="field" style={{ marginBottom: 0 }}>
          <span>Message to recipient (optional, shown after they enter the PIN)</span>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                    placeholder="Thanks for seeing this patient — please call me after reviewing." />
        </label>

        {/* Retention. Only the options this sender's plan will actually honour
            are offered, so nobody picks 90 days and quietly gets 30. */}
        <label style={{ display: 'block', marginTop: 12 }}>
          <span>Keep available for</span>
          <select value={expiryDays} onChange={(e) => setExpiryDays(Number(e.target.value))}>
            {[7, 14, 30, 60, 90]
              .filter((d) => d <= maxExpiryDays)
              .map((d) => (
                <option key={d} value={d}>{d} days</option>
              ))}
          </select>
          {maxExpiryDays < 90 && (
            <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
              Up to 90 days on unlimited sending.
            </span>
          )}
        </label>
      </div>

      <button
        className="btn btn-primary btn-block btn-large"
        style={{ marginTop: 16 }}
        onClick={onSubmit}
        disabled={!validForSend || stage !== 'idle'}
      >
        {stage === 'creating'  && 'Preparing…'}
        {stage === 'uploading' && 'Uploading files…'}
        {stage === 'sending'   && 'Sending…'}
        {stage === 'idle'      && 'Send securely'}
      </button>
      {err && <div className="error" style={{ textAlign: 'center' }}>{err}</div>}
      {quotaHit && <QuotaNotice key="quota-hit" />}
      {quotaHit && <StoreUpsellLine key="quota-upsell" />}
      <p className="muted" style={{ textAlign: 'center', marginTop: 10 }}>
        Files expire after 7 days. Recipients need the PIN you set to open them.
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 3: sent confirmation
// ═══════════════════════════════════════════════════════════════════════════

function SentStep({ sentInfo = null, onAnother, onDashboard }) {
  const [copied, setCopied] = useState(null);
  const [postedToBridge, setPostedToBridge] = useState(false);
  const links = sentInfo?.links || [];

  // Opened from a Bridge chat conversation (?src=bridge_chat with the chat
  // tab as opener): offer to post the transfer link straight back into the
  // conversation's composer instead of copy/paste.
  const fromBridge =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('src') === 'bridge_chat' &&
    !!window.opener;

  const postToBridge = (link) => {
    try {
      window.opener.postMessage(
        { type: 'send_transfer_link', link },
        'https://app.intake.dental'
      );
      setPostedToBridge(true);
      logEvent('bridge_chat_link_posted', { source: 'sent_step' });
    } catch { /* opener gone — copy buttons still work */ }
  };

  return (
    <div className="narrow" style={{ margin: '40px auto' }}>
      <div className="card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 10 }}>✓</div>
        <h1>Sent!</h1>
        <p className="lead">
          {sentInfo?.codeReused
            ? <>We emailed the secure link to your recipient. <strong>They already have the code</strong> from
              last time, and their email tells them to use it.</>
            : <>We emailed the secure link to your recipient. Now <strong>text or call them the PIN</strong> —
              they'll need it to open the package.</>}
        </p>
        {sentInfo?.detailsSealed && (
          <p className="muted" style={{ fontSize: 13, margin: '-4px 0 16px' }}>
            🛡️ <strong>Extra Security:</strong> the title, message and file names are sealed with glyph encryption.
          </p>
        )}
        {sentInfo?.e2e && links.length > 0 && (
          <div style={{ textAlign: 'left', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '14px 16px', margin: '0 0 16px' }}>
            <p style={{ margin: '0 0 8px', fontSize: 14, color: '#0c4a6e' }}>
              <strong>Zero-knowledge links</strong> — these carry the decryption key in the link itself
              (the part after <code>#</code> never reaches our servers). Share them directly for the
              strongest privacy; the emailed link works too, protected by the PIN.
            </p>
            {links.map((l) => (
              <div key={l.email} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <span className="muted" style={{ fontSize: 13, flexShrink: 0 }}>{l.email}</span>
                <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 13 }}
                  onClick={() => { navigator.clipboard.writeText(l.link); setCopied(l.email); setTimeout(() => setCopied(null), 2000); }}>
                  {copied === l.email ? 'Copied ✓' : 'Copy link'}
                </button>
              </div>
            ))}
          </div>
        )}
        {fromBridge && links.length > 0 && (
          <div style={{ background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 10, padding: '14px 16px', margin: '0 0 16px', textAlign: 'left' }}>
            <p style={{ margin: '0 0 8px', fontSize: 14, color: '#155e75' }}>
              <strong>Came from Bridge?</strong> Post the secure link straight back into your chat — it lands in the message box, ready to send.
            </p>
            <button
              className="btn btn-primary"
              style={{ padding: '8px 14px', fontSize: 14 }}
              disabled={postedToBridge}
              onClick={() => postToBridge(links[0].link)}
            >
              {postedToBridge ? 'Posted to your chat ✓' : 'Post link to Bridge chat'}
            </button>
          </div>
        )}
        <button className="btn btn-primary" onClick={onAnother}>Send another</button>
        <button className="btn btn-secondary" style={{ marginLeft: 8 }} onClick={onDashboard}>View my transfers</button>
        {/* The two places a sent package goes next. Send is a pipe: the bytes
            expire; Store keeps them. Bridge is where the conversation about
            the case continues. Always shown, whatever brought them here. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 18, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
          <a
            className="btn btn-secondary"
            href="https://store.intake.dental/?src=send_sent"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logEvent('store_keep_click', { source: 'sent_step' })}
          >
            Keep a copy in your Store →
          </a>
          <a
            className="btn btn-secondary"
            href="https://intake.dental/bridge?src=send_sent"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logEvent('bridge_cta_click', { source: 'sent_step' })}
          >
            Message this office on Bridge →
          </a>
        </div>
      </div>
      {/* Asked here and nowhere else: this is the one moment the sender has
          just watched a case go out and knows the tool works. */}
      <ShareWithColleague />
    </div>
  );
}

function formatBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
